import { AgentPicker } from "@app/components/assistant/AgentPicker";
import type { LightAgentConfigurationType } from "@app/types/assistant/agent";
import { GLOBAL_AGENTS_SID } from "@app/types/assistant/assistant";
import type { RichAgentMentionCandidate } from "@app/types/assistant/mentions";
import { assertNeverAndIgnore } from "@app/types/shared/utils/assert_never";
import type { LightWorkspaceType } from "@app/types/user";
import type { DropdownMenuItemProps } from "@dust-tt/sparkle";
import {
  Avatar,
  Button,
  ButtonGroup,
  ButtonGroupDropdown,
  Check,
  ChevronDown,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Icon,
  InfoCircle,
  Play,
  Robot,
  TextArea,
  Tooltip,
} from "@dust-tt/sparkle";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type React from "react";
import { useEffect, useMemo, useState } from "react";

type ProjectTaskStartWorkingOptions = {
  customMessage?: string;
  agentConfigurationId?: string;
  goToConversation: boolean;
};

type ProjectTaskStartWorkingContext = "tasks_page" | "conversation";

function startRedirectMenuLabels(context: ProjectTaskStartWorkingContext): {
  goToConversation: MessageDescriptor;
  stay: MessageDescriptor;
  ariaLabel: MessageDescriptor;
} {
  switch (context) {
    case "conversation":
      return {
        goToConversation: msg`Open task conversation`,
        stay: msg`Stay in this conversation`,
        ariaLabel: msg`After start: open task conversation or stay here`,
      };
    case "tasks_page":
      return {
        goToConversation: msg`Redirect to conversation`,
        stay: msg`Stay on tasks`,
        ariaLabel: msg`After start: open conversation or stay on tasks`,
      };
    default:
      assertNeverAndIgnore(context);
      return {
        goToConversation: msg`Redirect to conversation`,
        stay: msg`Stay on tasks`,
        ariaLabel: msg`After start: open conversation or stay on tasks`,
      };
  }
}

export function PodTaskStartWorkingDropdown({
  owner,
  taskId,
  activeAgents,
  agentsLoading,
  disabled,
  disabledReason,
  isStarting,
  isFirstOnboardingTask = false,
  defaultGoToConversation = false,
  defaultAgentId = null,
  context = "tasks_page",
  onOpenChange,
  onStart,
  triggerClassName,
  /** Default `xs`. Use `icon-xs` for a compact icon-only trigger (e.g. inline in metadata). */
  triggerSize = "xs",
}: {
  owner: LightWorkspaceType;
  taskId: string;
  activeAgents: LightAgentConfigurationType[];
  agentsLoading: boolean;
  disabled: boolean;
  disabledReason?: string;
  isStarting: boolean;
  isFirstOnboardingTask?: boolean;
  defaultGoToConversation?: boolean;
  defaultAgentId?: string | null;
  context?: ProjectTaskStartWorkingContext;
  onOpenChange?: (open: boolean) => void;
  onStart: (options: ProjectTaskStartWorkingOptions) => Promise<void>;
  triggerClassName?: string;
  triggerSize?: "xs" | "icon-xs";
}) {
  const { t } = useLingui();
  const [startMenuOpen, setStartMenuOpen] = useState(false);
  const [startCustomMessage, setStartCustomMessage] = useState("");
  const [goToConversationAfterStart, setGoToConversationAfterStart] = useState(
    defaultGoToConversation
  );
  const [selectedStartAgent, setSelectedStartAgent] =
    useState<RichAgentMentionCandidate | null>(null);

  useEffect(() => {
    setGoToConversationAfterStart(defaultGoToConversation);
  }, [defaultGoToConversation]);

  const handleStartMenuOpenChange = (open: boolean) => {
    setStartMenuOpen(open);
    onOpenChange?.(open);
    if (open) {
      setStartCustomMessage("");
      const defaultAgent =
        (defaultAgentId &&
          activeAgents.find((a) => a.sId === defaultAgentId)) ||
        activeAgents.find((a) => a.sId === GLOBAL_AGENTS_SID.DUST) ||
        activeAgents[0] ||
        null;
      setSelectedStartAgent(defaultAgent);
    }
  };

  const handleDirectStart = async () => {
    await onStart({
      customMessage: undefined,
      agentConfigurationId: undefined,
      goToConversation: true,
    });
  };

  const handleConfirmStart = async () => {
    setStartMenuOpen(false);
    onOpenChange?.(false);
    await onStart({
      customMessage: startCustomMessage.trim() || undefined,
      agentConfigurationId: selectedStartAgent?.sId,
      goToConversation: goToConversationAfterStart,
    });
  };

  const isDefaultAgentUnavailable =
    !agentsLoading &&
    !!defaultAgentId &&
    defaultAgentId !== GLOBAL_AGENTS_SID.DUST &&
    !activeAgents.some((a) => a.sId === defaultAgentId);

  const redirectMenuLabels = startRedirectMenuLabels(context);
  const goToConversationLabel = t(redirectMenuLabels.goToConversation);
  const stayLabel = t(redirectMenuLabels.stay);

  const startRedirectMenuItems = useMemo((): DropdownMenuItemProps[] => {
    const check = (
      <Icon size="xs" visual={Check} className="text-muted-foreground" />
    );
    return [
      {
        label: goToConversationLabel,
        onSelect: (e: Event) => {
          e.preventDefault();
          setGoToConversationAfterStart(true);
        },
        endComponent: goToConversationAfterStart ? check : undefined,
      },
      {
        label: stayLabel,
        onSelect: (e: Event) => {
          e.preventDefault();
          setGoToConversationAfterStart(false);
        },
        endComponent: !goToConversationAfterStart ? check : undefined,
      },
    ];
  }, [goToConversationAfterStart, goToConversationLabel, stayLabel]);

  const selectedAgentName = selectedStartAgent?.name ?? t`Agent`;

  if (isFirstOnboardingTask && !disabled) {
    return (
      <Button
        icon={Play}
        size={triggerSize}
        variant="outline"
        className={triggerClassName}
        isLoading={isStarting}
        disabled={isStarting}
        isPulsing={!isStarting}
        tooltip={t`Start working on task`}
        onClick={() => void handleDirectStart()}
      />
    );
  }

  const triggerButton = disabled ? (
    <Tooltip
      label={
        disabledReason ?? t`Can't start work on this task in this state yet.`
      }
      trigger={
        <Button icon={Play} size={triggerSize} variant="outline" disabled />
      }
    />
  ) : (
    <DropdownMenu
      modal={false}
      open={startMenuOpen}
      onOpenChange={handleStartMenuOpenChange}
    >
      <DropdownMenuTrigger asChild>
        <Button
          icon={Play}
          size={triggerSize}
          variant="outline"
          className={triggerClassName}
          isLoading={isStarting}
          disabled={isStarting}
          isPulsing={isFirstOnboardingTask && !startMenuOpen}
          tooltip={t`Start working on task`}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96">
        <div className="flex flex-col gap-3 p-3">
          <TextArea
            id={`task-start-msg-${taskId}`}
            aria-label={t`Additional instructions for the agent`}
            placeholder={t`(optional) Add a custom message for the agent...`}
            value={startCustomMessage}
            rows={4}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              setStartCustomMessage(event.target.value)
            }
          />
          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0 flex-1">
              <AgentPicker
                owner={owner}
                agents={activeAgents}
                disabled={agentsLoading}
                isLoading={agentsLoading}
                mountPortal
                showDropdownArrow={false}
                showFooterButtons={false}
                side="bottom"
                size="xs"
                onItemClick={(agent) => setSelectedStartAgent(agent)}
                pickerButton={
                  <button
                    type="button"
                    aria-label={t`Selected agent: ${selectedAgentName}`}
                    className={cn(
                      "inline-flex box-border max-w-full min-w-0 items-center rounded-lg h-7 heading-xs px-2 gap-1.5 text-primary-900 transition-colors duration-200",
                      agentsLoading
                        ? "opacity-50 pointer-events-none"
                        : "cursor-pointer hover:bg-muted-background"
                    )}
                  >
                    {selectedStartAgent ? (
                      <Avatar
                        size="xxs"
                        visual={selectedStartAgent.pictureUrl}
                      />
                    ) : (
                      <Icon visual={Robot} size="xs" />
                    )}
                    <span className="grow truncate notranslate">
                      {selectedAgentName}
                    </span>
                    {isDefaultAgentUnavailable && (
                      <Tooltip
                        tooltipTriggerAsChild
                        trigger={
                          <span
                            className="flex items-center text-warning"
                            onPointerDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <Icon visual={InfoCircle} size="xs" />
                          </span>
                        }
                        label={t`This collaborative space's default agent isn't available to you, so @Azimut is used instead. Discuss with your collaborative space editors if you think this is an error.`}
                      />
                    )}
                    <Icon
                      visual={ChevronDown}
                      size="xs"
                      className="-mr-1 text-faint"
                    />
                  </button>
                }
              />
            </div>
            <ButtonGroup className="shrink-0">
              <Button
                label={t`Start working`}
                variant="outline"
                size="sm"
                className={isFirstOnboardingTask ? "z-10" : ""}
                isLoading={isStarting}
                isPulsing={isFirstOnboardingTask}
                disabled={isStarting || !selectedStartAgent}
                onClick={() => void handleConfirmStart()}
              />
              <ButtonGroupDropdown
                align="end"
                items={startRedirectMenuItems}
                trigger={
                  <Button
                    variant="outline"
                    size="sm"
                    icon={ChevronDown}
                    disabled={isStarting || !selectedStartAgent}
                    aria-label={t(redirectMenuLabels.ariaLabel)}
                  />
                }
              />
            </ButtonGroup>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return triggerButton;
}
